import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Maximize2, X } from "lucide-react";
import SmoothImage from "./SmoothImage";

// Real-estate-style gallery with swipe, keyboard, thumbnails and fullscreen.
export default function PropertyGallery({
  images = [],
  aspect = "aspect-[4/3] md:aspect-[16/10]",
  testid = "property-gallery",
}) {
  const [i, setI] = useState(0);
  const [fs, setFs] = useState(false);
  const n = images.length;
  const trackRef = useRef(null);

  const prev = useCallback(() => setI((v) => (v - 1 + n) % n), [n]);
  const next = useCallback(() => setI((v) => (v + 1) % n), [n]);

  // Keyboard nav (and ESC to exit fullscreen)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "Escape") setFs(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prev, next]);

  // Lock body scroll while fullscreen is open
  useEffect(() => {
    if (fs) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [fs]);

  // Touch / swipe
  const touch = useRef({ x: 0, t: 0 });
  const onStart = (e) => { touch.current = { x: e.touches[0].clientX, t: Date.now() }; };
  const onEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touch.current.x;
    if (Math.abs(dx) > 40) (dx < 0 ? next : prev)();
  };

  if (n === 0) return null;

  return (
    <div className="relative" data-testid={testid}>
      {/* Main image with sliding track */}
      <div
        className={`relative w-full overflow-hidden rounded-[18px] md:rounded-[22px] bg-[color:var(--alpha-bg-soft)] shadow-xl alpha-ring-gradient ${aspect}`}
        onTouchStart={onStart}
        onTouchEnd={onEnd}
      >
        <div
          ref={trackRef}
          className="absolute inset-0 flex transition-transform duration-[550ms] ease-[cubic-bezier(.22,.9,.31,1)]"
          style={{ transform: `translateX(-${i * 100}%)` }}
        >
          {images.map((img, idx) => (
            <div key={img.src} className="relative shrink-0 w-full h-full">
              <SmoothImage
                src={img.src}
                alt={img.alt}
                loading={idx === 0 ? "eager" : "lazy"}
                className="absolute inset-0 w-full h-full object-cover"
                draggable={false}
              />
            </div>
          ))}
        </div>

        {/* Gradient edge readability */}
        <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-black/25 to-transparent pointer-events-none" />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />

        {/* Counter */}
        <div className="absolute left-3 top-3 md:left-4 md:top-4 text-[11px] uppercase tracking-[0.2em] bg-white/85 backdrop-blur px-2.5 py-1 rounded-full text-[color:var(--alpha-purple-800)] font-semibold">
          {i + 1} / {n}
        </div>

        {/* Fullscreen button */}
        <button
          onClick={() => setFs(true)}
          aria-label="Open fullscreen gallery"
          className="absolute right-3 top-3 md:right-4 md:top-4 w-10 h-10 rounded-full bg-white/90 hover:bg-white text-[color:var(--alpha-purple-700)] flex items-center justify-center shadow-lg transition-transform hover:scale-105"
          data-testid={`${testid}-fullscreen`}
        >
          <Maximize2 className="w-4 h-4" />
        </button>

        {/* Prev/Next */}
        {n > 1 && (
          <>
            <button onClick={prev} aria-label="Previous photo" className="absolute left-2 md:left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 hover:bg-white text-[color:var(--alpha-purple-700)] flex items-center justify-center shadow-lg transition-transform hover:scale-105" data-testid={`${testid}-prev`}>
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button onClick={next} aria-label="Next photo" className="absolute right-2 md:right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 hover:bg-white text-[color:var(--alpha-purple-700)] flex items-center justify-center shadow-lg transition-transform hover:scale-105" data-testid={`${testid}-next`}>
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}

        {/* Progress dots */}
        {n > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.map((img, idx) => (
              <button
                key={img.src || `dot-${idx}`}
                onClick={() => setI(idx)}
                aria-label={`Go to photo ${idx + 1}`}
                className={`h-1.5 rounded-full transition-all ${idx === i ? "w-6 bg-white" : "w-1.5 bg-white/60 hover:bg-white/85"}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Thumbnail strip */}
      {n > 1 && (
        <div className="mt-3 md:mt-4 grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(n, 6)}, minmax(0, 1fr))` }}>
          {images.slice(0, 6).map((img, idx) => (
            <button
              key={img.src}
              onClick={() => setI(idx)}
              aria-label={`Show photo ${idx + 1}`}
              className={`relative aspect-[4/3] rounded-lg md:rounded-xl overflow-hidden ring-2 transition-all ${
                idx === i ? "ring-[color:var(--alpha-purple-600)] scale-[1.02]" : "ring-transparent hover:ring-[color:var(--alpha-purple-200)]"
              }`}
              data-testid={`${testid}-thumb-${idx}`}
            >
              <img src={img.src} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
              {idx !== i && <div className="absolute inset-0 bg-white/25" />}
              {n > 6 && idx === 5 && (
                <div className="absolute inset-0 bg-[color:var(--alpha-purple-800)]/85 text-white flex items-center justify-center font-display font-semibold">
                  +{n - 5}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Fullscreen lightbox */}
      {fs && (
        <div className="fixed inset-0 z-[70] bg-black/95 flex items-center justify-center p-3 md:p-8 alpha-fade-up" role="dialog" aria-modal="true" data-testid={`${testid}-lightbox`} onClick={(e) => e.target === e.currentTarget && setFs(false)}>
          <button onClick={() => setFs(false)} aria-label="Close" className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur text-white flex items-center justify-center transition-colors">
            <X className="w-5 h-5" />
          </button>
          <div className="absolute top-5 left-5 text-white/80 text-sm font-semibold">{i + 1} / {n}</div>
          <div className="relative w-full max-w-6xl aspect-[16/10] max-h-[85vh]" onTouchStart={onStart} onTouchEnd={onEnd}>
            <img src={images[i].src} alt={images[i].alt} className="absolute inset-0 w-full h-full object-contain" />
          </div>
          {n > 1 && (
            <>
              <button onClick={prev} aria-label="Previous" className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur text-white flex items-center justify-center">
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button onClick={next} aria-label="Next" className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur text-white flex items-center justify-center">
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/80 text-sm max-w-md text-center px-4">{images[i].alt}</div>
        </div>
      )}
    </div>
  );
}
